import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { GripVertical, Scissors, Trash2, RotateCcw, FileText, GitBranch, Pencil, Sparkles, Undo2, Users } from 'lucide-react';

const STATUS_COLORS = {
  scheduled: 'bg-blue-50 border-blue-200 text-blue-700',
  in_progress: 'bg-amber-50 border-amber-200 text-amber-700',
  completed: 'bg-green-50 border-green-200 text-green-700',
  rolled_over: 'bg-gray-50 border-gray-200 text-gray-500',
};

const STATUS_LABELS = {
  scheduled: '待开始',
  in_progress: '进行中',
  completed: '已完成',
  rolled_over: '已滚动',
};

const ROLLED_NEXT_DAY_CLASS = 'bg-purple-50 border-purple-200 text-purple-700';

function ScheduleTaskCard({
  schedule,
  orderTotals,
  isOverlay = false,
  disabled = false,
  hasChildren = false,
  childCount = 0,
  descendantCount = 0,
  onDelete,
  onSplit,
  onExportProcess,
  onEditNote,
  onWithdrawSplit,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `board-task-${schedule._id}`,
    disabled,
    data: {
      type: 'board-task',
      scheduleId: schedule._id,
      schedule,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isRolledNextDay = !!schedule.rolled_from_schedule_id;
  const isSplitChild = !!schedule.parent_schedule_id;
  const isCompleted = schedule.status === 'completed';
  const isCustom = schedule.source === 'custom' || !schedule.production_order_number;
  const isSplitParent = hasChildren;

  const statusClass = isRolledNextDay
    ? ROLLED_NEXT_DAY_CLASS
    : (STATUS_COLORS[schedule.status] || STATUS_COLORS.scheduled);

  const totals = orderTotals || {
    totalCompleted: schedule.completed_quantity || 0,
    totalScrapped: schedule.scrapped_quantity || 0,
    totalPlanned: schedule.assigned_quantity || 0,
  };
  const { totalCompleted, totalScrapped, totalPlanned } = totals;

  const progress = totalPlanned > 0
    ? Math.round((totalCompleted / totalPlanned) * 100)
    : 0;

  const tooltipText = isCustom
    ? `${schedule.step_name}\n${schedule.finished_product_code}${schedule.finished_product_name ? ' · ' + schedule.finished_product_name : ''}${schedule.specification ? '\n规格: ' + schedule.specification : ''}\n数量: ${schedule.assigned_quantity} 件\n状态: ${isRolledNextDay ? '滚动' : (STATUS_LABELS[schedule.status] || schedule.status)}${schedule.notes ? '\n备注: ' + schedule.notes : ''}`
    : `${schedule.step_name}\n${schedule.production_order_number} · ${schedule.finished_product_code}\n${schedule.finished_product_name || ''}${schedule.specification ? '\n规格: ' + schedule.specification : ''}\n订单累计: ${totalCompleted}/${totalPlanned} 件 (${progress}%)${totalScrapped > 0 ? ` · 不良 ${totalScrapped}` : ''}\n本日: ${schedule.completed_quantity || 0}/${schedule.assigned_quantity || 0} 件\n状态: ${isRolledNextDay ? '滚动' : (STATUS_LABELS[schedule.status] || schedule.status)}${schedule.notes ? '\n备注: ' + schedule.notes : ''}`;

  const cardContent = (
    <div
      ref={setNodeRef}
      style={style}
      {...(disabled ? {} : listeners)}
      {...attributes}
      className={`
        p-1.5 sm:p-2 mb-1 sm:mb-2 border rounded-md sm:rounded-lg text-xs
        ${disabled ? 'cursor-not-allowed opacity-80' : 'cursor-grab active:cursor-grabbing'}
        ${statusClass}
        ${isDragging || isOverlay ? 'shadow-lg ring-2 ring-blue-400 opacity-90 z-50' : ''}
      `}
    >
      {/* Row 1: Order number + Step name + Status badge */}
      <div className="flex items-center gap-1 sm:gap-1.5 mb-1 sm:mb-1.5">
        <GripVertical className="h-3 w-3 sm:h-3.5 sm:w-3.5 opacity-40 shrink-0" />
        {isCustom ? (
          <span className="text-[9px] sm:text-[10px] text-red-700 font-semibold shrink-0 bg-red-100/60 px-1 py-0.5 rounded flex items-center gap-0.5">
            <Sparkles className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
            自定义
          </span>
        ) : (
          <span className="text-[9px] sm:text-[10px] text-blue-700 font-semibold shrink-0 bg-blue-100/50 px-1 py-0.5 rounded" title={`${schedule.production_order_number}-${String(schedule.item_index + 1).padStart(3, '0')}`}>
            {schedule.production_order_number}-{String(schedule.item_index + 1).padStart(3, '0')}
          </span>
        )}
        {isSplitParent && (
          <Badge variant="outline" className="text-[8px] sm:text-[9px] h-3.5 sm:h-4 px-1 shrink-0 bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300" title={`已拆分为 ${childCount} 个任务`}>
            <Users className="h-2 w-2 sm:h-2.5 sm:w-2.5 mr-0.5" />
            已拆分{childCount > 0 ? `·${childCount}` : ''}
          </Badge>
        )}
        <span className="font-semibold truncate flex-1 text-[11px] sm:text-[13px] leading-tight" title={schedule.step_name}>
          {schedule.step_name}
        </span>
        {isRolledNextDay ? (
          <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 shrink-0 bg-purple-100 text-purple-700 border-purple-300">
            <RotateCcw className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5" />
            滚动
          </Badge>
        ) : isSplitChild ? (
          <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 shrink-0 bg-cyan-100 text-cyan-700 border-cyan-300">
            <GitBranch className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5" />
            拆分
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[9px] sm:text-[10px] h-4 sm:h-5 px-1 sm:px-1.5 shrink-0">
            {STATUS_LABELS[schedule.status] || schedule.status}
          </Badge>
        )}
      </div>

      {/* Row 2: Product code + name */}
      <div className="text-[10px] sm:text-[11px] truncate mb-0.5" title={`${schedule.finished_product_code}${schedule.finished_product_name ? ' · ' + schedule.finished_product_name : ''}`}>
        <span className="font-medium">{schedule.finished_product_code}</span>
        {schedule.finished_product_name && (
          <span className="ml-1 opacity-80">{schedule.finished_product_name}</span>
        )}
      </div>

      {/* Row 3: Specification (if exists) */}
      {schedule.specification && (
        <div className="text-[10px] sm:text-[11px] text-amber-700 font-medium truncate mb-0.5 sm:mb-1" title={schedule.specification}>
          规格: {schedule.specification}
        </div>
      )}

      {/* Row 4: Quantity + actions */}
      <div className="flex items-center justify-between mt-0.5 sm:mt-1">
        <span className="text-[10px] sm:text-[11px] shrink-0 opacity-70 truncate">
          本日{schedule.completed_quantity || 0}/{schedule.assigned_quantity || 0}
          <span className="hidden sm:inline"> · 累计{totalCompleted}/{totalPlanned}</span>
        </span>
        {!isOverlay && !disabled && (
          <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onEditNote?.(schedule); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="p-0.5 sm:p-1 hover:bg-black/5 rounded"
              title="编辑备注/数量"
            >
              <Pencil className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </button>
            {!isCustom && (
              <button
                onClick={(e) => { e.stopPropagation(); onExportProcess?.(schedule); }}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-0.5 sm:p-1 hover:bg-black/5 rounded"
                title="导出工序文件"
              >
                <FileText className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              </button>
            )}
            {!isCompleted && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onSplit?.(schedule); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="p-0.5 sm:p-1 hover:bg-black/5 rounded"
                  title="拆分"
                >
                  <Scissors className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </button>
                {isSplitChild ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); onWithdrawSplit?.(schedule, descendantCount); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="p-0.5 sm:p-1 hover:bg-black/5 rounded text-cyan-700 hover:text-cyan-800"
                    title={`撤回拆分${descendantCount > 0 ? `（将同时撤销 ${descendantCount} 个子拆分）` : ''}`}
                  >
                    <Undo2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete?.(schedule._id); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="p-0.5 sm:p-1 hover:bg-black/5 rounded"
                    title="删除"
                  >
                    <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Progress bar */}
      {progress > 0 && progress < 100 && (
        <div className="mt-1.5 h-1.5 bg-black/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          {cardContent}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs whitespace-pre-line"
        >
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ScheduleTaskCard;
